import { useEffect } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/lang";

// Translates page text to Spanish on the fly when Español is chosen (skips sections already written in Spanish).
const SKIP = 'script,style,noscript,textarea,code,svg,[data-i18n="manual"],[translate="no"]';
const STORE = "bsn-tr-es";
const cache = new Map(Object.entries(JSON.parse(localStorage.getItem(STORE) || "{}")));
const originals = new Map();
const applied = new WeakMap();

const save = () => {
  const entries = [...cache.entries()].slice(-3000);
  try { localStorage.setItem(STORE, JSON.stringify(Object.fromEntries(entries))); } catch { /* full */ }
};

const wanted = (t) => t && t.trim().length > 1 && /[A-Za-z]{2}/.test(t);

const collect = (root) => {
  const items = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.parentElement?.closest(SKIP)) continue;
    if (applied.get(n) === n.nodeValue) continue;
    if (wanted(n.nodeValue)) items.push({ node: n, attr: null });
  }
  root.querySelectorAll("input[placeholder],textarea[placeholder],img[alt]").forEach((el) => {
    if (el.closest('[data-i18n="manual"]')) return;
    const attr = el.tagName === "IMG" ? "alt" : "placeholder";
    if (applied.get(el) === el.getAttribute(attr)) return;
    if (wanted(el.getAttribute(attr))) items.push({ node: el, attr });
  });
  return items;
};

const read = (it) => (it.attr ? it.node.getAttribute(it.attr) : it.node.nodeValue);
const write = (it, v) => {
  if (!originals.has(it.node)) originals.set(it.node, { attr: it.attr, value: read(it) });
  if (it.attr) it.node.setAttribute(it.attr, v); else it.node.nodeValue = v;
  applied.set(it.node, v);
};

const translateRoot = async (root, isActive) => {
  originals.forEach((_, n) => { if (!n.isConnected) originals.delete(n); });
  const items = collect(root);
  const keyOf = (it) => read(it).trim();
  const need = [...new Set(items.map(keyOf).filter((k) => !cache.has(k)))];
  for (let i = 0; i < need.length; i += 40) {
    const chunk = need.slice(i, i + 40);
    try {
      const { data } = await api.post("/translate", { texts: chunk });
      chunk.forEach((k, j) => data.translations[j] && cache.set(k, data.translations[j]));
    } catch { /* keep English if translation fails */ }
  }
  if (need.length) save();
  if (!isActive()) return;
  items.forEach((it) => {
    const raw = read(it);
    const es = cache.get(raw.trim());
    if (es) write(it, raw.replace(raw.trim(), es));
  });
};

const restore = () => {
  originals.forEach((o, node) => {
    if (o.attr) node.setAttribute(o.attr, o.value); else node.nodeValue = o.value;
    applied.delete(node);
  });
  originals.clear();
};

export const AutoTranslate = ({ rootId }) => {
  const { lang } = useLang();
  useEffect(() => {
    const root = document.getElementById(rootId);
    if (!root) return undefined;
    if (lang !== "es") { restore(); return undefined; }
    let active = true;
    let timer;
    const run = () => { clearTimeout(timer); timer = setTimeout(() => translateRoot(root, () => active), 250); };
    const obs = new MutationObserver(run);
    obs.observe(root, { childList: true, subtree: true, characterData: true });
    run();
    return () => { active = false; clearTimeout(timer); obs.disconnect(); };
  }, [lang, rootId]);
  return null;
};
